/**
 * PURPOSE: Gestionar las canchas de las sedes del evento desde el Centro (FTR-0028 D-07).
 * RESPONSIBILITIES: Listar, crear, renombrar, reordenar, retirar y reactivar canchas de las sedes
 *   que el evento puede usar.
 *
 *   POR QUÉ ESTÁ AQUÍ. Las cuatro vistas programan por cancha: el Calendario hace un carril por
 *   cancha y la vista Por Sede tiene una columna para ella. Sin una forma de crearlas, `sede_cancha`
 *   se queda vacía y esas dos vistas quedan a medias — se puede elegir sede pero nunca cancha. El
 *   backend ya expone el CRUD completo; esto es su interfaz.
 *
 *   RETIRAR NO ES BORRAR. `DELETE` pone `state = 0`: los partidos ya programados conservan su
 *   ubicación y la cancha sigue listada en gris, pero deja de ofrecerse para nuevas asignaciones.
 *   Borrarla de verdad dejaría partidos históricos sin lugar.
 *
 *   SOLO LAS SEDES DEL EVENTO. La lista es la de `GET /context`, que ya aplica la misma regla que
 *   acepta la programación: lo que se ofrece es lo que se acepta.
 * COLLABORATORS: ProgrammingCenterProvider.jsx, services/programmingCenterService.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/panels/CourtsPanel.jsx
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiCheck, FiPlus, FiRotateCcw, FiSlash } from "react-icons/fi";
import { toast } from "react-hot-toast";

import { useProgrammingCenter } from "../ProgrammingCenterProvider.jsx";
import {
  createCourt,
  deactivateCourt,
  updateCourt,
} from "../../../../services/programmingCenterService.js";
import { programmingCenterErrorMessage } from "../../../../utils/programmingCenterErrors.js";
import SidePanel from "../components/SidePanel.jsx";

const inputClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-800 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100";

const CourtsPanel = ({ open, onClose }) => {
  const { context, scope, invalidateContext, rows } = useProgrammingCenter();
  const [idsede, setIdsede] = useState("");
  const [name, setName] = useState("");
  const [working, setWorking] = useState(false);

  const venues = useMemo(() => context?.venues || [], [context]);

  useEffect(() => {
    if (!open) return;
    setName("");
    setIdsede((current) => current || String(venues[0]?.idsede || ""));
  }, [open, venues]);

  const venue = venues.find((item) => Number(item.idsede) === Number(idsede)) || null;

  /** Cuántos partidos usan ya esta cancha: retirarla no los mueve, pero conviene decirlo. */
  const usageOf = (idcancha) =>
    rows.filter((row) => Number(row.idcancha) === Number(idcancha)).length;

  const run = async (work, success) => {
    setWorking(true);
    try {
      await work();
      // Las canchas viajan dentro del contexto: es lo que alimenta los selectores de las vistas.
      await invalidateContext();
      toast.success(success);
    } catch (error) {
      toast.error(programmingCenterErrorMessage(error, "Canchas"));
    } finally {
      setWorking(false);
    }
  };

  const add = () => {
    const clean = name.trim();
    if (!clean) {
      toast.error("Indica un nombre para la cancha.");
      return;
    }
    run(
      () => createCourt({ ...scope, idsede: Number(idsede), name: clean }),
      `Cancha «${clean}» creada.`
    ).then(() => setName(""));
  };

  const rename = (court, nextName) => {
    const clean = String(nextName || "").trim();
    if (!clean || clean === court.name) return;
    run(
      () =>
        updateCourt({
          ...scope,
          idsede: Number(idsede),
          idcancha: court.idcancha,
          changes: { name: clean },
        }),
      "Cancha renombrada."
    );
  };

  const retire = (court) => {
    const used = usageOf(court.idcancha);
    const warning = used
      ? `\n\n${used} partidos la tienen asignada: conservarán su ubicación, pero la cancha dejará de ofrecerse.`
      : "";
    if (!window.confirm(`¿Retirar la cancha «${court.name}»?${warning}`)) return;
    run(
      () => deactivateCourt({ ...scope, idsede: Number(idsede), idcancha: court.idcancha }),
      "Cancha retirada."
    );
  };

  const reactivate = (court) =>
    run(
      () =>
        updateCourt({
          ...scope,
          idsede: Number(idsede),
          idcancha: court.idcancha,
          changes: { state: 1 },
        }),
      "Cancha reactivada."
    );

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Canchas de las sedes"
      subtitle="Los carriles del calendario y la columna «Cancha» salen de aquí"
    >
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">Sede</span>
        <select
          value={idsede}
          onChange={(event) => setIdsede(event.target.value)}
          className={inputClass}
          aria-label="Sede de las canchas"
        >
          {venues.map((item) => (
            <option key={item.idsede} value={item.idsede}>
              {item.name}
            </option>
          ))}
        </select>
      </label>

      {venue ? (
        <>
          <div className="mt-3 flex items-end gap-2">
            <label className="flex flex-1 flex-col gap-1">
              <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">
                Nueva cancha
              </span>
              <input
                type="text"
                maxLength={64}
                value={name}
                onChange={(event) => setName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") add();
                }}
                placeholder="Ej. Cancha 1"
                className={inputClass}
                aria-label="Nombre de la nueva cancha"
              />
            </label>
            <button
              type="button"
              onClick={add}
              disabled={working}
              className="flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-black text-white transition hover:bg-blue-700 disabled:opacity-50"
            >
              <FiPlus className="text-sm" />
              Agregar
            </button>
          </div>

          <ul className="mt-4 flex flex-col gap-1.5">
            {(venue.courts || []).map((court) => {
              const active = Number(court.state) === 1;
              const used = usageOf(court.idcancha);
              return (
                <li
                  key={court.idcancha}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
                    active ? "border-slate-200 bg-white" : "border-amber-200 bg-amber-50"
                  }`}
                >
                  <input
                    type="text"
                    defaultValue={court.name}
                    maxLength={64}
                    disabled={working || !active}
                    onBlur={(event) => rename(court, event.target.value)}
                    aria-label={`Nombre de ${court.name}`}
                    className="min-w-0 flex-1 rounded-lg border border-transparent bg-transparent px-2 py-1 text-sm font-black text-slate-800 outline-none transition hover:border-slate-200 focus:border-blue-400 disabled:text-slate-400"
                  />
                  <span className="shrink-0 text-[11px] font-bold text-slate-400">
                    {used ? `${used} partidos` : "sin uso"}
                  </span>
                  {active ? (
                    <button
                      type="button"
                      onClick={() => retire(court)}
                      disabled={working}
                      title="Retirar la cancha"
                      className="shrink-0 rounded-lg border border-slate-200 p-1.5 text-slate-500 transition hover:border-rose-300 hover:text-rose-600 disabled:opacity-50"
                    >
                      <FiSlash className="text-sm" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => reactivate(court)}
                      disabled={working}
                      title="Reactivar la cancha"
                      className="shrink-0 rounded-lg border border-amber-300 p-1.5 text-amber-700 transition hover:bg-amber-100 disabled:opacity-50"
                    >
                      <FiRotateCcw className="text-sm" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>

          {!(venue.courts || []).length ? (
            <p className="mt-3 rounded-xl border border-dashed border-slate-300 p-4 text-center text-xs font-bold text-slate-500">
              Esta sede no tiene canchas. Sin ellas el calendario le dará un solo carril y los
              partidos solo podrán distinguirse por su hora.
            </p>
          ) : null}

          <p className="mt-3 flex items-start gap-2 text-[11px] font-bold text-slate-500">
            <FiCheck className="mt-0.5 shrink-0 text-xs" />
            Retirar una cancha no mueve ningún partido: los que ya la tienen conservan su ubicación
            y la cancha queda listada en ámbar, pero deja de ofrecerse.
          </p>
        </>
      ) : (
        <p className="mt-4 text-sm font-bold text-slate-500">
          Este evento no tiene sedes disponibles para programar.
        </p>
      )}
    </SidePanel>
  );
};

export default CourtsPanel;
